import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Button,
  Column,
  Copy,
  Flex,
  Row,
  Square,
  Stack,
  SvgMagnifyingGlass,
  Squish,
  Loader,
} from '@grubhubprod/gh-cookbook-react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { Input } from 'src/components/Input/Input';
import Table from 'src/components/Table/Table';
import {
  ISearchParams,
  SearchStatus,
  SearchResourceOptions,
  SHORT_SEARCH_DELAY,
} from 'src/utilities/useSearchHooks';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import {
  SortableColumn,
  SortFields,
  SortOrder,
} from 'src/components/Table/SortableColumn';
import { isFunction } from 'src/utilities/is-function';

export type ColumnOptions = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12; // same as cookbook value
export const ENTERPRISE_SETUP_TABLE_DEFAULT_HEIGHT = '180px';
export interface HeaderColumn {
  columnName?: string;
  columnWidth: ColumnOptions;
  gutter?: 0 | 4 | 8 | 12 | 24;
  sortField?: SortFields;
  sortOrder?: SortOrder;
  isDefaultSort?: boolean;
}
interface IEnterpriseSetupTableProps<T> {
  corpId: string;
  searchPlaceholder: string;
  noResultsMessage: string;
  addButtonText?: string;
  searchResource: SearchResourceOptions;
  rowElementRender: (
    rowData: T,
    isEditingRow: boolean,
    toggleEditState: (id?: string) => void,
    updateSearch: (searchParams?: ISearchParams) => void,
    key?: string
  ) => JSX.Element;
  footerAddItemRender?: (
    toggleAddState: () => void,
    updateSearch: (searchParams?: ISearchParams) => void
  ) => JSX.Element;
  hideFooter?: boolean;
  headerColumns: HeaderColumn[];
  tableMaxHeight?: string;
  hasItemsCb?: (hasItems: boolean) => void;
  // TODO make resultsList type as T[] when refactoring SearchResourceOptions type
  resultsListCb?: (resultsList: unknown[]) => void;
}

export interface IEnterpriseSetupTableRowProps {
  isEditing: boolean;
  updateSearch: (searchParams?: ISearchParams) => void;
  toggleEditState: (id?: string) => void;
}

interface SearchFormFields {
  searchQuery: string;
}

export const EnterpriseSetupTable = forwardRef(SetupTable) as <T>(
  props: IEnterpriseSetupTableProps<T> & {
    ref?: React.ForwardedRef<HTMLElement>;
  }
) => ReturnType<typeof SetupTable>;

function SetupTable<T>(
  {
    corpId,
    searchPlaceholder,
    noResultsMessage,
    addButtonText = 'Add',
    headerColumns,
    hideFooter,
    searchResource,
    rowElementRender,
    footerAddItemRender,
    tableMaxHeight = ENTERPRISE_SETUP_TABLE_DEFAULT_HEIGHT,
    hasItemsCb,
    resultsListCb,
  }: IEnterpriseSetupTableProps<T>,
  ref
): JSX.Element {
  const {
    doSearch,
    resultsList,
    searchStatus,
    totalResults,
    hasMoreResults,
    loadMoreNumber,
    hasNoResultsWithQuery,
    hasNoResultsWithoutQuery,
  } = searchResource(corpId);

  const [itemLastEditedTimeMap, setitemLastEditedTimeMap] = useState({});

  const getInitialSortState = () => {
    const defaultSortColumn = headerColumns.find(
      (column) => column.isDefaultSort
    );

    if (defaultSortColumn) {
      return {
        field: defaultSortColumn.sortField,
        order: defaultSortColumn.sortOrder,
      };
    }

    return {
      field: null,
      order: null,
    };
  };
  const [previousSortState, setPreviousSortState] =
    useState(getInitialSortState);

  const { register: searchFormRegister, handleSubmit: handleSearchFormSubmit } =
    useForm<SearchFormFields>();

  const [isAddingItem, setIsAddingItem] = useState(false);
  const [editingState, setEditingState] = useState({
    isEditing: false,
    idBeingEdited: null,
  });

  const currentSearchQuery = useRef(null);

  useImperativeHandle(ref, () => ({
    updateSearch: doSearch,
  }));

  useEffect(() => {
    if (corpId) {
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  useEffect(() => {
    if (hasItemsCb && !currentSearchQuery.current) {
      hasItemsCb(resultsList.length > 0);
    }

    if (isFunction(resultsListCb)) {
      resultsListCb(resultsList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsList]);

  function toggleEditState(id?: string) {
    if (id) {
      setEditingState({
        isEditing: true,
        idBeingEdited: id,
      });
    } else {
      setEditingState(({ idBeingEdited }) => {
        // update last edited time for key/rerendering purposes
        // timeout is so that search is ideally updated before updating the time
        setTimeout(() => {
          setitemLastEditedTimeMap((previousMap) => {
            return {
              ...previousMap,
              [idBeingEdited]: Date.now(),
            };
          });
        }, SHORT_SEARCH_DELAY);

        setitemLastEditedTimeMap((previousMap) => {
          return {
            ...previousMap,
            [idBeingEdited]: Date.now(),
          };
        });

        return { isEditing: false, idBeingEdited: null };
      });
    }
  }

  function toggleAddState(toggle?: boolean): void {
    if (typeof toggle === 'boolean') {
      setIsAddingItem(toggle);
      return;
    }
    setIsAddingItem(!isAddingItem);
  }

  function executeSearch({ searchQuery }: SearchFormFields): void {
    const searchParams: ISearchParams = { page: 1 };
    currentSearchQuery.current = null;

    if (previousSortState.field && previousSortState.order) {
      searchParams.sort_field = previousSortState.field;
      searchParams.sort_order = previousSortState.order;
    }

    if (searchQuery) {
      searchParams.query = searchQuery;
      currentSearchQuery.current = searchQuery;
    }

    doSearch(searchParams);
  }

  function loadMoreResults(): void {
    const searchParams: ISearchParams = {};

    if (previousSortState.field && previousSortState.order) {
      searchParams.sort_field = previousSortState.field;
      searchParams.sort_order = previousSortState.order;
    }

    if (currentSearchQuery.current) {
      searchParams.query = currentSearchQuery.current;
    }

    doSearch(searchParams, true);
  }

  function sortSearchResults(sortField: SortFields, sortOrder: SortOrder) {
    setPreviousSortState({
      field: sortField,
      order: sortOrder,
    });

    const searchParams: ISearchParams = {
      page: 1,
      sort_field: sortField,
      sort_order: sortOrder,
    };

    if (currentSearchQuery.current) {
      searchParams.query = currentSearchQuery.current;
    }

    doSearch(searchParams);
  }

  return (
    <>
      <Row gutter={0} as="form">
        <Column tablet={5} gutter={0}>
          <Stack x="xx-sm">
            <Input
              name="searchQuery"
              id="searchQuery"
              ariaLabel={searchPlaceholder}
              placeholder={searchPlaceholder}
              prefixIcon={
                <Box color="secondaryText">
                  <SvgMagnifyingGlass size="20px" />
                </Box>
              }
              ref={searchFormRegister}
            />
          </Stack>
        </Column>
        <Column tablet={2} gutter={8}>
          <Button
            variant="secondary"
            buttonSize="medium"
            block
            disabled={hasNoResultsWithoutQuery}
            onClick={handleSearchFormSubmit(executeSearch)}
          >
            Search
          </Button>
        </Column>
      </Row>
      <Stack y="x-sm">
        <Flex justifyContent="flex-end">
          <Copy variant="body" color="secondaryText">
            {resultsList.length} of {totalResults}
          </Copy>
        </Flex>
      </Stack>
      <Table
        isSignupOrSetupTable
        headerInsets="sm"
        isLoading={searchStatus === SearchStatus.LOADING}
        header={() => (
          <Row gutter={0}>
            {headerColumns.map(
              ({
                columnName,
                columnWidth,
                gutter = 0,
                sortField,
                sortOrder,
              }) => {
                const isColumnSortable = sortField && sortOrder;
                return (
                  <Column
                    key={`${columnName}-${columnWidth}`}
                    phone={columnWidth}
                    gutter={gutter}
                  >
                    {isColumnSortable ? (
                      <SortableColumn
                        label={columnName}
                        sortField={sortField}
                        previousSortField={previousSortState.field}
                        previousSortOrder={previousSortState.order}
                        onSortColumnClick={sortSearchResults}
                      />
                    ) : (
                      columnName && (
                        <Copy variant="body" color="secondaryText">
                          {columnName}
                        </Copy>
                      )
                    )}
                  </Column>
                );
              }
            )}
          </Row>
        )}
        bodyMaxHeight={tableMaxHeight}
        body={() => (
          <>
            {hasNoResultsWithoutQuery && (
              <NoResultsMsg message={noResultsMessage} />
            )}
            {hasNoResultsWithQuery && (
              <NoResultsMsg
                message={`No results matching '${currentSearchQuery.current}'`}
              />
            )}
            {resultsList.map((result, index) => (
              <Squish
                key={`${result.id}-${itemLastEditedTimeMap[result.id]}`}
                borderBottom={
                  index === resultsList.length - 1 ? 'none' : '1px solid'
                }
                borderColor="lightLine"
              >
                {rowElementRender(
                  result,
                  editingState.idBeingEdited === result.id,
                  toggleEditState,
                  doSearch,
                  `${result.id}-${itemLastEditedTimeMap[result.id]}`
                )}
              </Squish>
            ))}
            {hasMoreResults && (
              <Squish inset="sm" borderTop="1px solid" borderColor="lightLine">
                <Flex alignItems="center" justifyContent="center">
                  {searchStatus === SearchStatus.LOADING && <Loader />}
                  {searchStatus === SearchStatus.COMPLETE && (
                    <Button variant="tertiary" onClick={loadMoreResults}>
                      Load {loadMoreNumber} more
                    </Button>
                  )}
                  {searchStatus === SearchStatus.ERROR && (
                    <Button variant="tertiary" onClick={loadMoreResults}>
                      <Copy variant="body" fontFamily="default">
                        Something went wrong loading your results.{' '}
                        <Copy variant="body" color="interactive">
                          Try again
                        </Copy>
                      </Copy>
                    </Button>
                  )}
                </Flex>
              </Squish>
            )}
          </>
        )}
        showFooter={!hideFooter}
        footerMinHeight="auto"
        justifyFooter="flex-start"
        footer={() => (
          <Square inset="sm" width="100%">
            {isAddingItem && footerAddItemRender ? (
              footerAddItemRender(toggleAddState, doSearch)
            ) : (
              <Button
                variant="secondary"
                disabled={editingState.isEditing}
                onClick={() => toggleAddState()}
              >
                {addButtonText}
              </Button>
            )}
          </Square>
        )}
      />
    </>
  );
}
